import React from 'react';
import { isSSR } from '@dwarvesf/react-utils';
import Layout from '../../components/Layout';
import NewsRead from './NewsRead';

function fetchNews(id, fetch) {
  return new Promise((resolve, reject) => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `query NewsByPub($publications: String) {
          news(publications: $publications, incrementCount: false, active: true) {
            items {
              title id date author blurb blurb_image_uid blurb_expanded_image_uid article
              article_image_uid article_image_uid_1 article_image_uid_2 article_image_uid_3 article_image_uid_4
              article_image_caption publications tags
            }
          }
        }`,
        variables: { publications: String(id) },
      }),
    })
      .then(res => res.json())
      .then(({ data }) => {
        const news = data && data.news && data.news.items;
        resolve(news);
      })
      .catch(reject);
  });
}

function getImageURL(uid) {
  const imageURI =
    uid.indexOf('https') !== -1
      ? uid.replace('https://', 'http://')
      : `https://dxfpracgmlczp.cloudfront.net/${uid}`;
  return encodeURI(imageURI);
}

function fetchNewsWithId(publicationId, id, fetch) {
  return new Promise((resolve, reject) => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `query NewsRead($id: Int, $publications: String, $isSSR: Boolean) {
          news(id: $id, publications: $publications, isSSR: $isSSR, incrementCount: false) {
            items {
              title id date author blurb blurb_image_uid blurb_expanded_image_uid article
              article_image_uid article_image_uid_1 article_image_uid_2 article_image_uid_3 article_image_uid_4
              article_image_caption article_image_caption_1 article_image_caption_2 article_image_caption_3
              article_image_caption_4 publications tags view_count
            }
          }
        }`,
        variables: {
          id: parseInt(id, 10),
          publications: String(publicationId),
          isSSR: isSSR(),
        },
      }),
    })
      .then(res => res.json())
      .then(({ data }) => {
        if (!data || data.news.items.length === 0) {
          reject(new Error('No such article'));
        }
        const news = data && data.news && data.news.items;
        resolve(news);
      })
      .catch(reject);
  });
}

function fetchPublication(id, fetch) {
  return new Promise((resolve, reject) => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `{publications(id:${id}){id, name, banner_image_uid, mast_head_uid, write_up, short_name}}`,
      }),
    })
      .then(res => res.json())
      .then(({ data }) => {
        if (data.publications.length === 0) {
          reject(new Error('No such publication'));
        }
        resolve(data.publications[0]);
      })
      .catch(reject);
  });
}

function extractPublicationId(path) {
  // For news read, the path would be '/publications/pub-id/news/news-id'
  const s1 = path.substring(14, path.length);
  const publicationId = s1.substring(0, s1.indexOf('/'));
  return publicationId;
}

function getFirstImage(article) {
  if (article.article_image_uid) return article.article_image_uid;
  if (article.article_image_uid_1) return article.article_image_uid_1;
  if (article.article_image_uid_2) return article.article_image_uid_2;
  if (article.article_image_uid_3) return article.article_image_uid_3;
  if (article.article_image_uid_4) return article.article_image_uid_4;
  // this is potential case not handled
  return null;
}

function fetchAds(id, fetch) {
  return new Promise((resolve, reject) => {
    fetch('/graphql', {
      body: JSON.stringify({
        query: `{ ads(publications: "${id}", active:true, currentIndex:0){
                  items{
                        id, title, image_uid, image_url, type, position
                      }
                  pageInfo {
                    hasNextPage
                    hasPreviousPage
                    totalCount
                    totalPages
                    currentIndex
                   }
                }}`,
      }),
    })
      .then(res => res.json())
      .then(({ data }) => {
        resolve(data.ads.items.filter(ad => ad.image_uid));
      })
      .catch(reject);
  });
}

async function action(context) {
  const { fetch, pathname, params } = context;
  const articleId = params.id;
  const publicationId = extractPublicationId(pathname);
  let articles;
  let publication;
  let articleOfInterest;
  let ads;

  // safeguard for invalid params in URL
  if (!/^\d*$/.test(articleId) || !/^\d*$/.test(publicationId)) {
    return { redirect: '/not-found' };
  }
  try {
    await new Promise((resolve, reject) => {
      Promise.all([
        fetchPublication(publicationId, fetch),
        fetchNews(publicationId, fetch),
        fetchAds(publicationId, fetch),
        fetchNewsWithId(publicationId, articleId, fetch),
      ])
        .then(results => {
          publication = results[0];
          // console.log(publication);
          articles = results[1];
          ads = results[2];
          articleOfInterest = results[3][0];
          resolve('Successfully fetched data');
        })
        .catch(err => {
          reject(err.message);
        });
    });
  } catch (err) {
    return { redirect: '/not-found' };
  }

  // console.log(`articleOfInterest.title`, articleOfInterest.title);
  return {
    chunks: ['newsRead'],
    ogtitle: articleOfInterest.title,
    title: articleOfInterest.title,
    description: articleOfInterest.title,
    image: getImageURL(getFirstImage(articleOfInterest)),
    site:
      publication &&
      publication.name &&
      `@${publication.name.replace(/ /g, '')}`,
    card: 'summary_large_image',
    url: `http://${publication.short_name}.tradelinkmedia.biz${pathname}`,
    component: (
      <Layout publication={publication}>
        <NewsRead
          allNews={articles}
          article={articleOfInterest}
          pathname={pathname}
          publication={publication}
          fetch={context.fetch}
          ads={ads}
        />
      </Layout>
    ),
  };
}

export default action;
